import React, { useEffect, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { attendanceAPI } from '../services/api';
import { Position } from '../types';
import { showCustomAlert } from './CustomAlert';

const Positions: React.FC = () => {
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState<Position | null>(null);
  const [error, setError] = useState('');

  const [form, setForm] = useState<{ name: string; description: string }>({
    name: '',
    description: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      // Prikazujemo i aktivne i neaktivne (da status kolona ima smisla)
      const data = await attendanceAPI.getPositions();
      setPositions(data);
    } catch (e: any) {
      console.error('Error fetching positions:', e);
      showCustomAlert.error(e?.response?.data?.message || 'Greška pri učitavanju pozicija');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setError('');
    setForm({ name: '', description: '' });
    setModalVisible(true);
  };

  const openEdit = (p: Position) => {
    setEditing(p);
    setError('');
    setForm({ name: p.name || '', description: p.description || '' });
    setModalVisible(true);
  };

  const submit = async () => {
    try {
      setError('');
      if (!form.name.trim()) {
        setError('Naziv pozicije je obavezan.');
        return;
      }
      const payload = { name: form.name.trim(), description: form.description.trim() || undefined };
      if (editing) {
        await attendanceAPI.updatePosition(editing._id, payload);
        showCustomAlert.success('Pozicija je ažurirana');
      } else {
        await attendanceAPI.createPosition(payload);
        showCustomAlert.success('Pozicija je dodata');
      }
      setModalVisible(false);
      fetchData();
    } catch (e: any) {
      console.error('Error saving position:', e);
      setError(e?.response?.data?.message || 'Greška pri čuvanju pozicije');
    }
  };

  const deletePosition = (p: Position) => {
    confirmDialog({
      header: 'Potvrda',
      icon: 'pi pi-exclamation-triangle',
      message: `Da li ste sigurni da želite da obrišete poziciju "${p.name}"?`,
      acceptLabel: 'Obriši',
      rejectLabel: 'Otkaži',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await attendanceAPI.deletePosition(p._id);
          showCustomAlert.success('Pozicija je obrisana');
          fetchData();
        } catch (e: any) {
          showCustomAlert.error(e?.response?.data?.message || 'Greška pri brisanju pozicije');
        }
      }
    });
  };

  const statusTemplate = (p: Position) => {
    const label = p.isActive ? 'Aktivna' : 'Neaktivna';
    const cls = p.isActive ? 'bg-green-500' : 'bg-gray-500';
    return (
      <span className={`inline-flex align-items-center gap-2 px-3 py-1 text-white text-sm border-round ${cls}`}>
        <i className={`pi ${p.isActive ? 'pi-check' : 'pi-times'}`} />
        {label}
      </span>
    );
  };

  const actionsTemplate = (p: Position) => {
    return (
      <div className="flex gap-2 justify-content-end">
        <Button
          icon="pi pi-pencil"
          rounded
          severity="warning"
          className="p-button-sm"
          tooltip="Izmeni"
          tooltipOptions={{ position: 'left' }}
          onClick={() => openEdit(p)}
        />
        <Button
          icon="pi pi-trash"
          rounded
          severity="danger"
          className="p-button-sm"
          tooltip="Obriši"
          tooltipOptions={{ position: 'left' }}
          onClick={() => deletePosition(p)}
        />
      </div>
    );
  };

  return (
    <div className="p-4">
      <Card>
        <h2 className="m-0">Upravljanje pozicijama</h2>
        <p className="text-600 mt-2 mb-4">
          Pozicije se koriste pri dodavanju radnika. Možete dodati nove pozicije prema potrebi.
        </p>

        <div className="flex justify-content-between align-items-center mb-4 p-3 border-1 border-round surface-border bg-white">
          <Button label="Dodaj poziciju" icon="pi pi-plus" onClick={openCreate} severity="success" />
          <Button label="Osveži" icon="pi pi-refresh" onClick={fetchData} severity="secondary" />
        </div>

        <DataTable
          value={positions}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          currentPageReportTemplate="Prikaz {first} do {last} od ukupno {totalRecords} pozicija"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          emptyMessage="Nema pozicija"
          sortMode="multiple"
          className="p-datatable-sm"
        >
          <Column field="name" header="Naziv" sortable />
          <Column field="description" header="Opis" sortable body={(p: Position) => p.description || ''} />
          <Column header="Status" body={statusTemplate} style={{ width: '160px' }} />
          <Column header="" body={actionsTemplate} style={{ width: '140px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={editing ? 'Izmeni poziciju' : 'Nova pozicija'}
        visible={modalVisible}
        onHide={() => setModalVisible(false)}
        style={{ width: '560px' }}
        draggable={false}
        className="p-fluid"
      >
        {error && <Message severity="error" text={error} className="mb-3" />}

        <div className="field">
          <label className="block mb-2">
            Naziv pozicije <span className="text-red-500">*</span>
          </label>
          <InputText
            value={form.name}
            placeholder="npr. Operater na proizvodnji"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>

        <div className="field">
          <label className="block mb-2">Opis</label>
          <InputTextarea
            value={form.description}
            placeholder="Opcioni opis pozicije..."
            rows={4}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>

        <div className="flex justify-content-end gap-2 mt-2">
          <Button label="Otkaži" className="p-button-secondary p-button-outlined" onClick={() => setModalVisible(false)} />
          <Button label="Sačuvaj" icon="pi pi-check" severity="success" onClick={submit} />
        </div>
      </Dialog>

      <ConfirmDialog />
    </div>
  );
};

export default Positions;


